import React, { useCallback, use, useEffect, useId, useMemo, useState } from 'react'
import { MemoryRouter, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { produce } from 'immer'
import noop from 'lodash/noop'
import { AssetPickerPopover } from '@chilipiper/design-system/src/new'

interface ElementValue {
  onClose: () => void
  value: React.ReactNode
}

const SubAppContext = React.createContext<{
  removeElement: (id: string) => void
  setElement: (id: string, value: ElementValue) => void
}>({ setElement: noop, removeElement: noop })

export const SubAppProvider = ({
  children,
  sharedProvidersRender,
}: {
  children: React.ReactNode
  sharedProvidersRender: (children: React.ReactNode) => React.ReactNode
}) => {
  const [elements, setElements] = useState<Record<string, ElementValue>>({})

  const setElement = useCallback((id: string, value: ElementValue) => {
    return setElements(prevElements => {
      return produce(prevElements, draft => {
        draft[id] = value
      })
    })
  }, [])

  const removeElement = useCallback((id: string) => {
    return setElements(prevElements => {
      return produce(prevElements, draft => {
        delete draft[id]
      })
    })
  }, [])

  const value = useMemo(() => ({ setElement, removeElement }), [setElement, removeElement])
  const entries = useMemo(() => Object.entries(elements), [elements])
  const keysToRender = useMemo(() => Object.keys(elements).slice(-1), [elements])

  const [router] = useState(() =>
    createBrowserRouter([
      {
        path: '*',
        element: sharedProvidersRender(children),
      },
    ])
  )

  return (
    <SubAppContext value={value}>
      <RouterProvider router={router} />
      {entries.map(([key, element]) => (
        <MemoryRouter key={key}>
          {sharedProvidersRender(
            <AssetPickerPopover.CreateModal
              isOpen
              onOpenChange={isOpen => {
                if (!isOpen) {
                  element.onClose()
                  return removeElement(key)
                }
              }}
            >
              {() => <>{keysToRender.includes(key) ? element.value : null}</>}
            </AssetPickerPopover.CreateModal>
          )}
        </MemoryRouter>
      ))}
    </SubAppContext>
  )
}

export const SubApp = ({
  children,
  isOpen,
  onClose,
}: {
  children: (closeSubApp: () => void) => React.ReactNode
  isOpen: boolean
  onClose: () => void
}) => {
  const id = useId()
  const { setElement, removeElement } = use(SubAppContext)

  useEffect(() => {
    if (isOpen) {
      setElement(id, {
        onClose,
        value: children(() => {
          onClose()
          removeElement(id)
        }),
      })
    } else {
      removeElement(id)
    }
  }, [isOpen])

  return null
}
